import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../../api/api";
import Modal from "react-modal";

Modal.setAppElement("#root");

function flattenTree(items, parent = null, depth = 0) {
  return items.reduce((acc, item) => {
    const newItem = { ...item, parent, depth };
    acc.push(newItem);
    if (item.children && item.children.length > 0) {
      acc.push(...flattenTree(item.children, item.id, depth + 1));
    }
    return acc;
  }, []);
}

function collectByType(content, type, collector) {
  const results = [];
  function traverse(nodes) {
    nodes.forEach((node) => {
      if (node.type === type) {
        results.push(collector(node));
      }
      if (node.children) traverse(node.children);
    });
  }
  traverse(content);
  return results;
}

export default function ListBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaModal, setMediaModal] = useState({
    isOpen: false,
    type: "",
    items: [],
  });
  const [mediaLoading, setMediaLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    console.log("REACT_APP_API_URL:", process.env.REACT_APP_BACKEND_API_URL);
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/blog");
      const fetchedData = response?.data?.data || response?.data;
      console.log("API Response:", fetchedData);
      if (!Array.isArray(fetchedData)) {
        toast.error("Invalid data format received from API");
        setBlogs([]);
      } else {
        setBlogs(fetchedData);
      }
    } catch (err) {
      toast.error("Failed to fetch Blogs");
      console.error("Error fetching Blogs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Blog?")) return;
    try {
      await axios.delete(`/api/blog/${id}`);
      toast.success("Deleted successfully");
      setBlogs((prevBlogs) => prevBlogs.filter((blog) => blog._id !== id));
      fetchBlogs();
    } catch (err) {
      toast.error("Failed to delete Blog");
      console.error("Error deleting Blog:", err);
      fetchBlogs();
    }
  };

  const getDataByType = (content, type) => {
    if (!Array.isArray(content)) return "";
    const results = collectByType(content, type, (node) => node.data).filter(
      Boolean,
    );
    const fullText = results.join("\n") || "";
    // Split by newline and take only the first two lines
    const lines = fullText.split("\n").slice(0, 1);
    return lines.join("\n") || "";
  };

  const getMediaByType = (content, type) => {
    if (!Array.isArray(content)) {
      console.warn(`Content is not an array for type ${type}:`, content);
      return [];
    }
    return collectByType(content, type, (node) => node.data)
      .filter(Boolean)
      .map((url) => {
        try {
          if (url.startsWith("http")) {
            console.log(`Using absolute URL for ${type}:`, url);
            return url;
          }
          if (!process.env.REACT_APP_BACKEND_API_URL) {
            throw new Error("REACT_APP_API_URL is not defined");
          }
          const fullUrl = `${process.env.REACT_APP_BACKEND_API_URL}${url}`;
          console.log(`Constructed ${type} URL:`, fullUrl);
          return fullUrl;
        } catch (err) {
          console.error(`Invalid URL for ${type}:`, url, err);
          return "";
        }
      })
      .filter((url) => url);
  };

  const getBlogsList = (content) => {
    if (!Array.isArray(content)) {
      console.warn("Content is not an array:", content);
      return [];
    }
    const flattened = flattenTree(content);
    return flattened
      .filter(
        (item) =>
          item.type === "faq" && item.data && typeof item.data === "object",
      )
      .map((item) => ({
        question: item.data.question || "No question provided",
        answer: item.data.answer || "No answer provided",
        depth: item.depth,
        parent: item.parent,
        id:
          item.id ||
          `faq-${item.depth}-${Math.random().toString(36).substr(2, 9)}`, // Unique ID if not present
      }));
  };

  const openMediaModal = async (type, items) => {
    setMediaLoading(true);
    const validItems = items.filter((url) => {
      try {
        new URL(url);
        return true;
      } catch {
        console.warn(`Invalid media URL for ${type}: ${url}`);
        return false;
      }
    });
    if (validItems.length === 0) {
      toast.error(
        `No valid ${type.toLowerCase()} found. Ensure media URLs are correct, REACT_APP_API_URL is set to http://localhost:5000 in .env, and files exist in uploads/${type.toLowerCase()}/ folder.`,
      );
      setMediaLoading(false);
      return;
    }
    setMediaModal({ isOpen: true, type, items: validItems });
    setMediaLoading(false);
  };

  const closeMediaModal = () => {
    setMediaModal({ isOpen: false, type: "", items: [] });
    setMediaLoading(false);
  };

  const filteredBlogs = blogs.filter((blog) => {
    if (activeTab === "all") return true;
    if (activeTab === "published") return blog.status === "published";
    if (activeTab === "drafts") return blog.status === "draft";
    if (activeTab === "discount") return blog.discount;
    return true;
  });

  const renderMediaButton = (type, items, iconClass) => {
    if (items.length === 0) return null;
    return (
      <button
        onClick={() => openMediaModal(type, items)}
        onKeyDown={(e) => e.key === "Enter" && openMediaModal(type, items)}
        tabIndex={0}
        className="flex items-center text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300 text-xs transition-colors duration-200 underline"
        aria-label={`View ${type}`}
      >
        <i className={`${iconClass} mr-1 text-sm`} />
        {items.length} {type}
      </button>
    );
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <div className="text-center text-gray-500 py-10">Loading Blogs...</div>
      );
    }
    if (filteredBlogs.length === 0) {
      return (
        <div className="text-center text-gray-500 py-10">No Blogs found.</div>
      );
    }
    return (
      <>
        <div className="grid grid-cols-1 p-0 md:p-4">
          <div className="sm:-mx-6 lg:-mx-8">
            <div className="relative overflow-x-auto block w-full sm:px-6 lg:px-8">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 dark:bg-slate-700/20">
                  <tr>
                    <th scope="col" className="p-3">
                      <label className="custom-label">
                        <div className="bg-white dark:bg-slate-600/40 border border-slate-200 dark:border-slate-600 rounded w-5 h-5 inline-block text-center">
                          <input type="checkbox" className="hidden" />
                          <i className="icofont-verification-check hidden text-ms text-brand-500 dark:text-slate-200 leading-5" />
                        </div>
                      </label>
                    </th>
                    <th
                      scope="col"
                      className="p-3 text-xs font-medium tracking-wider text-left text-gray-700 dark:text-gray-400 uppercase min-w-[150px]"
                    >
                      Blog & Title
                    </th>
                    <th
                      scope="col"
                      className="p-3 text-xs font-medium tracking-wider text-left text-gray-700 dark:text-gray-400 uppercase min-w-[200px]"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="p-3 text-xs font-medium tracking-wider text-left text-gray-700 dark:text-gray-400 uppercase min-w-[150px]"
                    >
                      Meta Title
                    </th>
                    <th
                      scope="col"
                      className="p-3 text-xs font-medium tracking-wider text-left text-gray-700 dark:text-gray-400 uppercase min-w-[200px]"
                    >
                      Meta Description
                    </th>
                    <th
                      scope="col"
                      className="p-3 text-xs font-medium tracking-wider text-left text-gray-700 dark:text-gray-400 uppercase min-w-[150px]"
                    >
                      Key Features
                    </th>
                    <th
                      scope="col"
                      className="p-3 text-xs font-medium tracking-wider text-left text-gray-700 dark:text-gray-400 uppercase min-w-[120px]"
                    >
                      Media
                    </th>
                    <th
                      scope="col"
                      className="p-3 text-xs font-medium tracking-wider text-left text-gray-700 dark:text-gray-400 uppercase min-w-[120px]"
                    >
                      FAQs
                    </th>
                    <th
                      scope="col"
                      className="p-3 text-xs font-medium tracking-wider text-left text-gray-700 dark:text-gray-400 uppercase min-w-[100px]"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBlogs.map((blog) => {
                    const { _id, content, status } = blog;
                    const title = getDataByType(content, "title");
                    const desc = getDataByType(content, "description");
                    const metaT = getDataByType(content, "metaTitle");
                    const metaD = getDataByType(content, "metaDescription");
                    const keyF = getDataByType(content, "keyFeatures");
                    const images = getMediaByType(content, "image");
                    const videos = getMediaByType(content, "video");
                    const icons = getMediaByType(content, "icon");
                    const faqList = getBlogsList(content);
                    const firstImage = images[0] || "";

                    let statusClass = "";
                    let statusText = "";
                    if (status === "published") {
                      statusClass = "bg-green-600/5 text-green-600";
                      statusText = "Published";
                    } else if (status === "draft") {
                      statusClass = "bg-yellow-600/5 text-yellow-600";
                      statusText = "Draft";
                    } else {
                      statusClass = "bg-red-600/5 text-red-600";
                      statusText = "Unknown";
                    }

                    return (
                      <tr
                        key={_id}
                        className="bg-white border-b border-dashed dark:bg-gray-900 dark:border-gray-700/40"
                      >
                        <td className="w-4 p-4">
                          <label className="custom-label">
                            <div className="bg-white dark:bg-slate-600/40 border border-slate-200 dark:border-slate-600 rounded w-5 h-5 inline-block text-center">
                              <input type="checkbox" className="hidden" />
                              <i className="icofont-verification-check hidden text-ms text-brand-500 dark:text-slate-200 leading-5" />
                            </div>
                          </label>
                        </td>
                        <td className="p-3 text-sm font-medium dark:text-white">
                          <div className="flex items-center">
                            {firstImage ? (
                              <img
                                src={firstImage}
                                alt="Blog thumbnail"
                                className="me-2 h-8 w-8 object-cover rounded"
                                onError={(e) =>
                                  (e.target.src = "/fallback-image.png")
                                }
                                loading="lazy"
                              />
                            ) : (
                              <div className="me-2 h-8 w-8 inline-block bg-gray-200 dark:bg-gray-700 rounded" />
                            )}
                            <div className="self-center">
                              <h5 className="text-sm font-semibold text-slate-700 dark:text-gray-400">
                                {title || "-"}
                              </h5>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                          <div className="break-words max-w-[200px]">
                            {desc
                              ? desc.length > 100
                                ? desc.slice(0, 70) + "..."
                                : desc
                              : "-"}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                          <div className="break-words max-w-[150px]">
                            {metaT || "-"}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                          <div className="break-words max-w-[200px]">
                            {metaD || "-"}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                          <div className="break-words max-w-[150px]">
                            {keyF || "-"}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-500 dark:text-gray-400 space-y-2">
                          {renderMediaButton(
                            "Images",
                            images,
                            "fa-regular fa-image",
                          )}
                          {renderMediaButton(
                            "Videos",
                            videos,
                            "fa-solid fa-video",
                          )}
                          {renderMediaButton(
                            "Icons",
                            icons,
                            "fa-solid fa-bell",
                          )}
                          {images.length === 0 &&
                            videos.length === 0 &&
                            icons.length === 0 && <span>-</span>}
                        </td>
                        <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                          <button
                            onClick={() =>
                              setExpandedId(expandedId === _id ? null : _id)
                            }
                            onKeyDown={(e) =>
                              e.key === "Enter" &&
                              setExpandedId(expandedId === _id ? null : _id)
                            }
                            tabIndex={0}
                            className="text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300 text-sm transition-colors duration-200 underline"
                            aria-label={
                              expandedId === _id
                                ? "Hide FAQs"
                                : `Show FAQs (${faqList.length})`
                            }
                            aria-expanded={expandedId === _id}
                          >
                            {expandedId === _id
                              ? "Hide FAQs"
                              : `Show FAQs (${faqList.length})`}
                          </button>
                          {expandedId === _id && (
                            <ul
                              className="mt-2 space-y-2 text-sm text-gray-600 dark:text-gray-300 max-w-md"
                              aria-label="FAQ items"
                            >
                              {faqList.length > 0 ? (
                                faqList.map((f, idx) => (
                                  <li
                                    key={f.id} // Use unique id for key
                                    className="border-l-2 border-gray-200 dark:border-gray-600 pl-2"
                                    style={{ paddingLeft: `${f.depth * 1}rem` }}
                                  >
                                    <strong className="block font-semibold">
                                      {f.question || "No question"}{" "}
                                      {f.parent
                                        ? `(Child of ${f.parent})`
                                        : "(Parent)"}
                                    </strong>
                                    <span className="block break-words">
                                      {f.answer || "No answer"}
                                    </span>
                                  </li>
                                ))
                              ) : (
                                <li className="text-gray-500 dark:text-gray-400">
                                  No FAQ items available
                                </li>
                              )}
                            </ul>
                          )}
                        </td>
                        <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                          <button
                            onClick={() => navigate(`/edit-blog/${_id}`)}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            aria-label="Edit Blog"
                          >
                            <i className="icofont-ui-edit text-lg" />
                          </button>
                          <button
                            onClick={() => handleDelete(_id)}
                            className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 ml-2"
                            aria-label="Delete Blog"
                          >
                            <i className="icofont-ui-delete text-lg" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Pagination */}
        {/* <div className="flex justify-between mt-4">
          <div className="self-center">
            <p className="dark:text-slate-400">
              Showing 1 - {filteredBlogs.length} of {blogs.length}
            </p>
          </div>
          <div className="self-center">
            <ul className="inline-flex items-center -space-x-px">
              <li>
                <button className="py-2 px-3 ms-0 leading-tight text-gray-500 bg-white rounded-l-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
                  <i className="icofont-simple-left" />
                </button>
              </li>
              <li>
                <button className="py-2 px-3 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
                  1
                </button>
              </li>
              <li>
                <button
                  aria-current="page"
                  className="z-10 py-2 px-3 leading-tight text-brand-600 bg-brand-50 border border-brand-300 hover:bg-brand-100 hover:text-brand-700 dark:border-gray-700 dark:bg-gray-700 dark:text-white"
                >
                  2
                </button>
              </li>
              <li>
                <button className="py-2 px-3 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
                  3
                </button>
              </li>
              <li>
                <button className="py-2 px-3 leading-tight text-gray-500 bg-white rounded-r-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
                  <i className="icofont-simple-right" />
                </button>
              </li>
            </ul>
          </div>
        </div> */}
      </>
    );
  };

  return (
    <div className="ltr:flex flex-1 rtl:flex-row-reverse">
      <div className="page-wrapper relative ltr:ms-auto rtl:me-auto rtl:ms-0 w-[calc(100%-260px)] px-4 pt-[64px] duration-300">
        <div className="xl:w-full">
          <div className="flex flex-wrap">
            <div className="flex items-center py-4 w-full">
              <div className="w-full">
                <div className="">
                  <div className="flex flex-wrap justify-between">
                    <div className="items-center">
                      <h1 className="font-medium text-3xl block dark:text-slate-100">
                        Blogs
                      </h1>
                      <ol className="list-reset flex text-sm">
                        <li>
                          <a
                            href="#"
                            className="text-gray-500 dark:text-slate-400"
                          >
                            Excellent
                          </a>
                        </li>
                        <li>
                          <span className="text-gray-500 dark:text-slate-400 mx-2">
                            /
                          </span>
                        </li>
                        <li className="text-gray-500 dark:text-slate-400">
                          Admin
                        </li>
                        <li>
                          <span className="text-gray-500 dark:text-slate-400 mx-2">
                            /
                          </span>
                        </li>
                        <li className="text-primary-500 hover:text-primary-600 dark:text-primary-400">
                          Blogs
                        </li>
                      </ol>
                    </div>
                    {/* <div className="flex items-center">
                      <div className="today-date leading-5 mt-2 lg:mt-0 form-input w-auto rounded-md border inline-block border-primary-500/60 dark:border-primary-500/60 text-primary-500 bg-transparent px-3 py-1 focus:outline-none focus:ring-0 placeholder:text-slate-400/70 placeholder:font-normal placeholder:text-sm hover:border-primary-400 focus:border-primary-500 dark:focus:border-primary-500 dark:hover:border-slate-700">
                        <input
                          type="text"
                          className="dash_date border-0 focus:border-0 focus:outline-none"
                          readOnly
                          required
                        />
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="xl:w-full min-h-[calc(100vh-138px)] relative pb-14">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <div className="w-full relative mb-4">
                <div className="flex-auto p-0 md:p-4">
                  <div
                    className="mb-4 border-b border-gray-200 dark:border-slate-700"
                    data-fc-type="tab"
                  >
                    <ul
                      className="flex flex-wrap -mb-px text-sm font-medium text-center"
                      aria-label="Tabs"
                    >
                      <li className="me-2" role="presentation">
                        <button
                          className={`inline-block p-4 rounded-t-lg border-b-2 ${
                            activeTab === "all"
                              ? "border-brand-500 text-brand-500"
                              : "border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                          }`}
                          onClick={() => setActiveTab("all")}
                          type="button"
                          role="tab"
                          aria-controls="all"
                          aria-selected={activeTab === "all"}
                        >
                          All{" "}
                          <span className="text-slate-400">
                            ({blogs.length})
                          </span>
                        </button>
                      </li>
                      {/* <li className="me-2" role="presentation">
                        <button
                          className={`inline-block p-4 rounded-t-lg border-b-2 ${
                            activeTab === "published"
                              ? "border-brand-500 text-brand-500"
                              : "border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                          }`}
                          onClick={() => setActiveTab("published")}
                          type="button"
                          role="tab"
                          aria-controls="published"
                          aria-selected={activeTab === "published"}
                        >
                          Published{" "}
                          <span className="text-slate-400">
                            (
                            {
                              blogs.filter((b) => b.status === "published")
                                .length
                            }
                            )
                          </span>
                        </button>
                      </li>
                      <li className="me-2" role="presentation">
                        <button
                          className={`inline-block p-4 rounded-t-lg border-b-2 ${
                            activeTab === "drafts"
                              ? "border-brand-500 text-brand-500"
                              : "border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                          }`}
                          onClick={() => setActiveTab("drafts")}
                          type="button"
                          role="tab"
                          aria-controls="drafts"
                          aria-selected={activeTab === "drafts"}
                        >
                          Drafts{" "}
                          <span className="text-slate-400">
                            ({blogs.filter((b) => b.status === "draft").length})
                          </span>
                        </button>
                      </li>
                      <li role="presentation">
                        <button
                          className={`inline-block p-4 rounded-t-lg border-b-2 ${
                            activeTab === "discount"
                              ? "border-brand-500 text-brand-500"
                              : "border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                          }`}
                          onClick={() => setActiveTab("discount")}
                          type="button"
                          role="tab"
                          aria-controls="discount"
                          aria-selected={activeTab === "discount"}
                        >
                          On Discount{" "}
                          <span className="text-slate-400">
                            ({blogs.filter((b) => b.discount).length})
                          </span>
                        </button>
                      </li> */}
                    </ul>
                  </div>
                  <div className="flex flex-wrap gap-4 mb-3">
                    {/* <div className="mb-2 w-44">
                      <select
                        id="Category"
                        className="w-full rounded-md mt-1 border border-slate-300/60 dark:border-slate-700 dark:text-slate-300 bg-transparent px-3 py-2 focus:outline-none focus:ring-0 placeholder:text-slate-400/70 placeholder:font-normal placeholder:text-sm hover:border-slate-400 focus:border-primary-500 dark:focus:border-primary-500 dark:hover:border-slate-700"
                      >
                        <option className="dark:text-slate-700">
                          All Category
                        </option>
                        <option className="dark:text-slate-700">General</option>
                        <option className="dark:text-slate-700">
                          Technical
                        </option>
                        <option className="dark:text-slate-700">Support</option>
                      </select>
                    </div>
                    <div className="mb-2 w-36">
                      <select
                        id="Vendor"
                        className="w-full rounded-md mt-1 border border-slate-300/60 dark:border-slate-700 dark:text-slate-300 bg-transparent px-3 py-2 focus:outline-none focus:ring-0 placeholder:text-slate-400/70 placeholder:font-normal placeholder:text-sm hover:border-slate-400 focus:border-primary-500 dark:focus:border-primary-500 dark:hover:border-slate-700"
                      >
                        <option className="dark:text-slate-700">
                          All Vendors
                        </option>
                        <option className="dark:text-slate-700">
                          Vendor-1
                        </option>
                        <option className="dark:text-slate-700">
                          Vendor-2
                        </option>
                        <option className="dark:text-slate-700">
                          Vendor-3
                        </option>
                      </select>
                    </div>
                    <div className="ms-auto">
                      <form>
                        <div className="relative">
                          <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                            <i
                              data-lucide="search"
                              className="z-[1] w-5 h-5 stroke-slate-400"
                            />
                          </div>
                          <input
                            type="search"
                            id="blogSearch"
                            className="form-input w-52 rounded-md mt-1 border border-slate-300/60 dark:border-slate-700 dark:text-slate-300 bg-transparent px-3 py-2 focus:outline-none focus:ring-0 placeholder:text-slate-400/70 placeholder:font-normal placeholder:text-sm hover:border-slate-400 focus:border-primary-500 dark:focus:border-primary-500 dark:hover:border-slate-700 pl-10 p-2.5"
                            placeholder="search"
                          />
                        </div>
                      </form>
                    </div> */}
                    {/* <div>
                      <button
                        onClick={() => navigate("/addblog")}
                        className="inline-block focus:outline-none bg-brand-500 mt-1 text-white hover:bg-brand-600 hover:text-white text-md font-medium py-2 px-4 rounded"
                      >
                        Add Blog
                      </button>
                    </div> */}
                  </div>
                  <div id="myTabContent">
                    <div
                      className={`p-4 bg-gray-50 rounded-lg dark:bg-gray-900 ${
                        activeTab === "all" ? "block" : "hidden"
                      }`}
                      id="all"
                      role="tabpanel"
                      aria-labelledby="all-tab"
                    >
                      {renderTableContent()}
                    </div>
                    <div
                      className={`p-4 bg-gray-50 rounded-lg dark:bg-gray-800 ${
                        activeTab === "published" ? "block" : "hidden"
                      }`}
                      id="published"
                      role="tabpanel"
                      aria-labelledby="published-tab"
                    >
                      {renderTableContent()}
                    </div>
                    <div
                      className={`p-4 bg-gray-50 rounded-lg dark:bg-gray-800 ${
                        activeTab === "drafts" ? "block" : "hidden"
                      }`}
                      id="drafts"
                      role="tabpanel"
                      aria-labelledby="drafts-tab"
                    >
                      {renderTableContent()}
                    </div>
                    <div
                      className={`p-4 bg-gray-50 rounded-lg dark:bg-gray-800 ${
                        activeTab === "discount" ? "block" : "hidden"
                      }`}
                      id="discount"
                      role="tabpanel"
                      aria-labelledby="discount-tab"
                    >
                      {renderTableContent()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Modal
          isOpen={mediaModal.isOpen}
          onRequestClose={closeMediaModal}
          contentLabel={`Preview ${mediaModal.type}`}
          className="w-full max-w-[95vw] sm:max-w-3xl md:max-w-4xl mx-auto mt-6 sm:mt-16 bg-white dark:bg-gray-900 rounded-xl shadow-2xl outline-none border border-gray-200 dark:border-gray-700"
          overlayClassName="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
          aria={{
            labelledby: "media-modal-title",
            describedby: "media-modal-content",
          }}
        >
          <div className="relative p-6 sm:p-8">
            <button
              onClick={closeMediaModal}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-full p-2"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h3
              id="media-modal-title"
              className="text-2xl font-semibold text-gray-800 dark:text-white mb-6 border-b border-gray-200 dark:border-gray-700 pb-3"
            >
              {mediaModal.type} Preview
            </h3>
            <div
              id="media-modal-content"
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-h-[70vh] overflow-y-auto pr-2"
            >
              {mediaLoading ? (
                <div className="col-span-full flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-500"></div>
                </div>
              ) : mediaModal.items.length > 0 ? (
                mediaModal.items.map((url, idx) => (
                  <div
                    key={idx}
                    className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-200"
                  >
                    {mediaModal.type === "Videos" ? (
                      <video
                        src={url}
                        controls
                        className="w-full h-48 object-contain rounded-lg"
                        onError={(e) =>
                          (e.target.poster = "/fallback-video.png")
                        }
                        preload="metadata"
                        aria-label={`Video ${idx + 1}`}
                      />
                    ) : (
                      <img
                        src={url}
                        alt={`${mediaModal.type} ${idx + 1}`}
                        className="w-full h-48 object-contain rounded-lg"
                        onError={(e) => (e.target.src = "/fallback-image.png")}
                        loading="lazy"
                        aria-label={`${mediaModal.type} ${idx + 1}`}
                      />
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gray-900 bg-opacity-50 text-white text-xs p-2 text-center">
                      {mediaModal.type} {idx + 1}
                    </div>
                  </div>
                ))
              ) : (
                <p className="col-span-full text-center text-gray-500 dark:text-gray-400 py-8">
                  No valid {mediaModal.type.toLowerCase()} available
                </p>
              )}
            </div>
            <div className="mt-8 flex justify-end gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
              <button
                onClick={closeMediaModal}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                aria-label="Close modal"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
